import { Dashboard } from '@/common/dashboard/Dashboard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { DimRearrangeButton } from '@/common/entityFramework/entityPerspective/support/DimRearrangeButton';
import { DropdownSelect } from '@/common/form/formFields/DropdownSelect';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useState } from 'react';
import { InctvRbtTrendCard } from './InctvRbtTrendCard';

const metricOptions = [
  { value: 'SALES', displayName: 'Sales Amount' },
  { value: 'RBT', displayName: 'Rebate Amount' },
  { value: 'DISP', displayName: 'Disputed Amount' },
  { value: 'PYMT', displayName: 'Payment Amount' },
  { value: 'DUE', displayName: 'Due Amount' },
  { value: 'INTR', displayName: 'Interest Amount' },
];

// All Entity Filter Field Definitions
const entityFilterFieldDefs = [
  {
    field: 'rbtPeriodExpr',
    displayName: 'Rebate Periods',
    entity: 'PERIOD',
    code: 'RBT_PERIOD',
    module: 'sc',
    multiple: true,
    hidden: true,
  },
  {
    field: 'earnedPeriodExpr',
    displayName: 'Earned Periods',
    entity: 'PERIOD',
    code: 'EARNED_PERIOD',
    module: 'sc',
    multiple: true,
  },
  {
    field: 'prodExpr',
    displayName: 'Products',
    entity: 'NDC11_PROD',
    code: 'NDC11_PROD',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INCTV_RBT_LINE',
    parentFilterFkField: 'prodId',
  },
  {
    field: 'progExpr',
    displayName: 'Programs',
    entity: 'PROG',
    code: 'PROG',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INCTV_RBT',
    parentFilterFkField: 'progId',
  },
  {
    field: 'custExpr',
    displayName: 'Customers',
    entity: 'CUST',
    code: 'CUST',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INCTV_RBT',
    parentFilterFkField: 'custId',
  },
  {
    field: 'ctrtExpr',
    displayName: 'Contracts',
    entity: 'CTRT',
    code: 'CTRT',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'INCTV_RBT',
    parentFilterFkField: 'ctrtId',
  },
];

// All filters
const codeToFilter = Object.fromEntries(
  entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, { displayName: fieldDef.displayName }]),
);

// Default filters
const defaultVisibleFilters = entityFilterFieldDefs
  .filter((fieldDef) => !fieldDef.hidden)
  .map((fieldDef) => fieldDef.code);

export const InctvRbtFinDashboard = () => {
  const rsrc = useRsrc();

  const [earnedPeriodIds, setEarnedPeriodIds] = useState(false);
  const [rbtPeriodIds, setRbtPeriodIds] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState(null);
  const [visibleFilters, setVisibleFilters] = useNavStateSync('visibleFilters', defaultVisibleFilters);

  useEffect(() => {
    rsrc.data('sc', 'INCTV_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      setDefaultFormValues({
        metric: ['PYMT'],
        progExpr: null,
        rbtPeriodExpr: null,
        earnedPeriodExpr: ExprUtils.whereParams('id', res.selectedEarnedPeriodIds),
        earnedPeriodExprSelectedCount: res.selectedEarnedPeriodIds.length,
        earnedPeriodExprSelectedIds: res.selectedEarnedPeriodIds,
        earnedPeriodExprHeaderChecked: false,
        prodExpr: null,
        custExpr: null,
        ctrtExpr: null,
        xAxis: 'EARNED_PERIOD',
      });
      setEarnedPeriodIds(res.earnedPeriodIds);
      setRbtPeriodIds(res.rbtPeriodIds);
    });
  }, []);

  // Paid Periods can only be restricted once the filter options have loaded
  const fieldDefs = useMemo(
    () =>
      entityFilterFieldDefs.map((fieldDef) =>
        fieldDef.code === 'EARNED_PERIOD' ? { ...fieldDef, expr: ExprUtils.whereParams('id', earnedPeriodIds) }
        : fieldDef.code === 'RBT_PERIOD' ? { ...fieldDef, expr: ExprUtils.whereParams('id', rbtPeriodIds) }
        : fieldDef,
      ),
    [earnedPeriodIds, rbtPeriodIds],
  );

  // X-axis options for the dashboard charts
  const xAxisOptions = useMemo(
    () => fieldDefs.map((fieldDef) => ({ code: fieldDef.code, displayName: fieldDef.displayName, fieldDef })),
    [fieldDefs],
  );

  // Saved nav state may reference filters that no longer exist
  const activeFieldDefs = useMemo(
    () => visibleFilters.map((code) => fieldDefs.find((fieldDef) => fieldDef.code === code)).filter(Boolean),
    [visibleFilters, fieldDefs],
  );

  const resetDims = () => {
    setVisibleFilters(defaultVisibleFilters);
  };

  if (!defaultFormValues) return null;

  const augButtons = (buttons) => [
    buttons.unshift(
      <DimRearrangeButton
        key="filterRearrangeButton"
        setDims={setVisibleFilters}
        codeToDim={codeToFilter}
        activeDims={activeFieldDefs.map(({ code }) => ({ code }))}
      />,
    ),
  ];

  return (
    <Dashboard
      title="Incentive Rebate Financial Dashboard"
      titleIcon={fas.faFileInvoice}
      entity="INCTV_RBT"
      defaultFormValues={defaultFormValues}
      augButtons={augButtons}
      onReset={resetDims}
    >
      <FilterContainer>
        <DropdownSelect
          fieldDef={{
            field: 'metric',
            displayName: 'Metric',
            multiSelect: true,
            values: metricOptions,
          }}
        />
        {activeFieldDefs.map((fieldDef) => (
          <EntitySelect key={fieldDef.field} fieldDef={{ ...fieldDef, variant: 'outlined' }} />
        ))}
      </FilterContainer>
      <SpacerLayout>
        <Spacer weights={[2, 1]}>
          <InctvRbtTrendCard xAxisOptions={xAxisOptions} metricOptions={metricOptions} />
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
